import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field } from "@/components/AdminTools";
import {
  Lightbulb, MessageSquare, Share2, ChevronUp, Pin, Send, X, Plus,
  Twitter, Linkedin, Mail, Link as LinkIcon, Sparkles, ShieldCheck,
} from "lucide-react";

const KIND_META = {
  idea: {
    label: "Idea",
    icon: Lightbulb,
    tone: "bg-amber-50 text-amber-700 border-amber-200",
    iconBg: "bg-amber-100 text-amber-700 border-amber-200",
    intro: "Pitch a new service, vendor type, program, workflow, or feature. Use this to request a new vendor service for the Nodera network.",
  },
  feedback: {
    label: "Feedback",
    icon: MessageSquare,
    tone: "bg-blue-50 text-blue-700 border-blue-200",
    iconBg: "bg-blue-100 text-blue-700 border-blue-200",
    intro: "Share constructive feedback — what's working, what isn't, what could improve.",
  },
  share: {
    label: "Share",
    icon: Share2,
    tone: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconBg: "bg-emerald-100 text-emerald-700 border-emerald-200",
    intro: "Share Nodera with your network — drop a link below or use the channel buttons.",
  },
};

const STATUS_TONE = {
  open: "bg-slate-100 text-slate-700",
  under_review: "bg-blue-100 text-blue-700",
  planned: "bg-violet-100 text-violet-700",
  shipped: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
};

const KIND_FILTERS = [
  { v: "all", label: "All" },
  { v: "idea", label: "Ideas" },
  { v: "feedback", label: "Feedback" },
  { v: "share", label: "Share" },
];

const SHARE_LINK = typeof window !== "undefined"
  ? `${window.location.origin}/?utm_source=think-tank&utm_medium=share`
  : "https://nodera.health";
const SHARE_COPY = "Nodera Health — operational intelligence + cash-pay program infrastructure for clinics. Built for the people running the day-to-day.";

export default function PortalThinkTank() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("top");
  const [creating, setCreating] = useState(null); // null | "idea" | "feedback" | "share"
  const [openCommentsId, setOpenCommentsId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = { sort };
    if (filter !== "all") params.kind = filter;
    api.get("/think-tank/posts", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, sort]);

  const upvote = async (id) => {
    try {
      const r = await api.post(`/think-tank/posts/${id}/upvote`);
      setItems((rows) => rows.map((it) => it.id === id
        ? { ...it, has_upvoted: r.data.has_upvoted, upvotes: it.upvotes + r.data.delta }
        : it));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Resources · Community"
      title="Think Tank"
      intro="A community space for sharing ideas, capturing feedback, and amplifying Nodera within your network. Every post is visible to clinics, vendors, and the Nodera team. Upvote what resonates, comment to add context, and watch ideas move from open → planned → shipped."
    >
      {/* Request-a-vendor-service hero callout — replaces the old Request Services module. */}
      <div
        data-testid="think-tank-request-vendor-callout"
        className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 mb-5 flex items-start gap-4"
      >
        <span className="w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 text-amber-700 flex items-center justify-center shrink-0">
          <Lightbulb className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700">Need a vendor service that's not in the network?</div>
          <h2 className="font-display text-lg font-bold text-slate-900 mt-1">Request it here — under "New Idea"</h2>
          <p className="text-sm text-slate-700 mt-1 leading-relaxed">
            Use the <strong>+ New Idea</strong> button below to request a new vendor service, program type, or capability for the Nodera network. Our concierge sources matches, and the community upvotes which requests we tackle next.
          </p>
        </div>
        <button
          type="button"
          data-testid="think-tank-request-vendor-cta"
          onClick={() => setCreating("idea")}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-bold hover:bg-slate-800"
        >
          <Plus className="w-3 h-3" /> Request a vendor service
        </button>
      </div>

      {/* Action strip */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.v}
              type="button"
              data-testid={`think-tank-filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                filter === f.v
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-2 text-[11px] text-slate-400">·</span>
          <button
            type="button"
            data-testid="think-tank-sort-toggle"
            onClick={() => setSort(sort === "top" ? "new" : "top")}
            className="px-3 py-1 text-xs font-semibold rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {sort === "top" ? "Sort · Top" : "Sort · New"}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <KindBtn kind="idea" onClick={() => setCreating("idea")} />
          <KindBtn kind="feedback" onClick={() => setCreating("feedback")} />
          <KindBtn kind="share" onClick={() => setCreating("share")} />
        </div>
      </div>

      {creating && (
        <CreateForm
          kind={creating}
          onCancel={() => setCreating(null)}
          onSubmitted={() => { setCreating(null); load(); }}
        />
      )}

      {/* Share-channel quick row when no posts */}
      <ShareChannels />

      {loading ? (
        <div className="text-sm text-slate-500 mt-5">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center mt-3" data-testid="think-tank-empty">
          <Lightbulb className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold text-slate-900">No posts yet — be first</div>
          <p className="text-sm text-slate-500 mt-1">Pitch an idea, share feedback, or amplify Nodera with your network.</p>
        </div>
      ) : (
        <ul className="space-y-3 mt-3" data-testid="think-tank-list">
          {items.map((it) => (
            <PostRow
              key={it.id}
              post={it}
              onUpvote={() => upvote(it.id)}
              expanded={openCommentsId === it.id}
              onToggleComments={() => setOpenCommentsId(openCommentsId === it.id ? null : it.id)}
            />
          ))}
        </ul>
      )}
    </ToolPageShell>
  );
}

function KindBtn({ kind, onClick }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid={`think-tank-new-${kind}`}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border ${meta.tone} hover:bg-white`}
    >
      <Icon className="w-3 h-3" /> + New {meta.label}
    </button>
  );
}

function CreateForm({ kind, onCancel, onSubmitted }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const [form, setForm] = useState({ title: "", body: "", share_link: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/think-tank/posts", { ...form, kind });
      toast.success(`${meta.label} posted to Think Tank.`);
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid={`think-tank-form-${kind}`}
      onSubmit={submit}
      className={`rounded-2xl border-2 p-5 mb-5 ${meta.tone}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> New {meta.label}
        </div>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-slate-600 mb-3">{meta.intro}</p>
      <div className="space-y-3">
        <Field label="Title">
          <input
            required
            data-testid={`think-tank-form-title-${kind}`}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={kind === "share" ? "Why I love using Nodera" : kind === "feedback" ? "What works · what could improve" : "A new program category we'd love to see"}
            className={inputCls}
          />
        </Field>
        <Field label="Details">
          <textarea
            required
            rows={4}
            data-testid={`think-tank-form-body-${kind}`}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className={inputCls}
            placeholder={
              kind === "share"
                ? "Drop a quote we can include in the share copy, or just hit Post and use the channel buttons below."
                : kind === "feedback"
                ? "Specifics help: which screen, what behavior, what the impact is on your day."
                : "What problem does this solve? Who benefits? Any operational details we should consider?"
            }
          />
        </Field>
        {kind === "share" && (
          <Field label="Optional · share link (where you posted it)">
            <input
              data-testid="think-tank-form-share-link"
              value={form.share_link}
              onChange={(e) => setForm({ ...form, share_link: e.target.value })}
              placeholder="https://twitter.com/yourhandle/status/…"
              className={inputCls}
            />
          </Field>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <button type="button" onClick={onCancel} className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid={`think-tank-form-submit-${kind}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-bold hover:bg-slate-800 disabled:opacity-60"
        >
          <Send className="w-3 h-3" /> {saving ? "Posting…" : `Post ${meta.label}`}
        </button>
      </div>
    </form>
  );
}

function ShareChannels() {
  const tweetHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_COPY)}&url=${encodeURIComponent(SHARE_LINK)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_LINK)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("You should check out Nodera Health")}&body=${encodeURIComponent(SHARE_COPY + "\n\n" + SHARE_LINK)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_LINK);
      toast.success("Share link copied.");
    } catch {
      toast.error("Copy failed — link unavailable.");
    }
  };
  return (
    <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-4 mb-5" data-testid="think-tank-share-channels">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Share Nodera in one click
          </div>
          <p className="text-xs text-slate-600 mt-0.5">Help your network discover the operational layer for cash-pay programs.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={tweetHref} target="_blank" rel="noreferrer" data-testid="think-tank-share-twitter"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-800">
            <Twitter className="w-3 h-3" /> Twitter
          </a>
          <a href={linkedinHref} target="_blank" rel="noreferrer" data-testid="think-tank-share-linkedin"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 text-white px-3 py-1.5 text-xs font-semibold hover:bg-blue-800">
            <Linkedin className="w-3 h-3" /> LinkedIn
          </a>
          <a href={mailHref} data-testid="think-tank-share-email"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
            <Mail className="w-3 h-3" /> Email
          </a>
          <button type="button" onClick={copy} data-testid="think-tank-share-copy"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
            <LinkIcon className="w-3 h-3" /> Copy link
          </button>
        </div>
      </div>
    </div>
  );
}

function PostRow({ post, onUpvote, expanded, onToggleComments }) {
  const meta = KIND_META[post.kind] || KIND_META.idea;
  const Icon = meta.icon;
  return (
    <li
      data-testid={`think-tank-post-${post.id}`}
      className={`rounded-2xl bg-white border ${post.pinned ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`}
    >
      <div className="p-4 flex items-start gap-3">
        <button
          type="button"
          onClick={onUpvote}
          data-testid={`think-tank-upvote-${post.id}`}
          className={`shrink-0 w-12 rounded-xl flex flex-col items-center justify-center py-2 border transition ${
            post.has_upvoted
              ? "bg-amber-100 border-amber-300 text-amber-800"
              : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
          }`}
        >
          <ChevronUp className={`w-4 h-4 ${post.has_upvoted ? "fill-amber-700" : ""}`} />
          <span className="text-xs font-extrabold tabular-nums">{post.upvotes || 0}</span>
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border text-[10px] uppercase tracking-wider font-bold ${meta.iconBg}`}>
              <Icon className="w-2.5 h-2.5" /> {meta.label}
            </span>
            {post.status && post.status !== "open" && (
              <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[post.status] || "bg-slate-100 text-slate-700"}`}>
                {String(post.status).replace("_", " ")}
              </span>
            )}
            {post.pinned && (
              <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
                <Pin className="w-2.5 h-2.5" /> Pinned
              </span>
            )}
          </div>
          <h3 className="font-display text-base font-bold text-slate-900 mt-1.5">{post.title}</h3>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {post.author_name || "Anonymous"}
            {post.author_clinic ? ` · ${post.author_clinic}` : ""}
            {post.author_role === "admin" && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700 font-semibold">
                <ShieldCheck className="w-2.5 h-2.5" /> Nodera
              </span>
            )}
            <span className="mx-1">·</span>
            {new Date(post.created_at).toLocaleDateString()}
          </div>
          <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{post.body}</p>
          {post.share_link && (
            <a href={post.share_link} target="_blank" rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
              <LinkIcon className="w-3 h-3" /> View shared post
            </a>
          )}

          {post.admin_response && (
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
              <span className="font-bold uppercase tracking-wider text-[10px] block mb-1">Nodera response</span>
              {post.admin_response}
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid={`think-tank-comments-toggle-${post.id}`}
              onClick={onToggleComments}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
            >
              <MessageSquare className="w-3 h-3" />
              {post.comment_count || 0} {(post.comment_count === 1) ? "comment" : "comments"}
            </button>
          </div>

          {expanded && <CommentThread postId={post.id} />}
        </div>
      </div>
    </li>
  );
}

function CommentThread({ postId }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/think-tank/posts/${postId}/comments`)
      .then((r) => setComments(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/think-tank/posts/${postId}/comments`, { body });
      setBody("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`think-tank-comments-${postId}`}>
      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : comments.length === 0 ? (
        <div className="text-xs text-slate-500 mb-2">No comments yet — be first.</div>
      ) : (
        <ul className="space-y-2 mb-3">
          {comments.map((c) => (
            <li key={c.id} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5" data-testid={`think-tank-comment-${c.id}`}>
              <div className="text-[11px] text-slate-500">
                {c.author_name}
                {c.is_admin && (
                  <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700 font-semibold">
                    <ShieldCheck className="w-2.5 h-2.5" /> Nodera
                  </span>
                )}
                <span className="mx-1">·</span>
                {new Date(c.created_at).toLocaleString()}
              </div>
              <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex items-start gap-2">
        <input
          type="text"
          data-testid={`think-tank-comment-input-${postId}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment…"
          className={`${inputCls} flex-1`}
        />
        <button
          type="submit"
          disabled={submitting || !body.trim()}
          data-testid={`think-tank-comment-submit-${postId}`}
          className="inline-flex items-center gap-1 bg-slate-900 text-white rounded-full px-3 py-2 text-xs font-bold hover:bg-slate-800 disabled:opacity-50"
        >
          <Plus className="w-3 h-3" /> Post
        </button>
      </form>
    </div>
  );
}
